import type { FC } from 'react';
import { Alert, Button, Drawer, Skeleton, Space, Tag, Typography } from 'antd';
import {
  CalendarOutlined,
  EyeOutlined,
  PlusOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { PlanDetailTaskSubtasks } from '~/app/components/maintenance/preventive/PlanDetailPageContainer/components/PlanDetailTaskSubtasks/PlanDetailTaskSubtasks';
import {
  WORK_ORDER_UI,
  softBadgeStyle,
  executionResultTone,
} from '~/app/components/maintenance/work-orders/work-order-ui-colors';
import { useAuth } from '~/app/auth/context/AuthContext';
import { useWorkOrderDetail } from '~/app/hooks/maintenance-execution/useWorkOrderDetail';
import { useWorkOrderMutations } from '~/app/hooks/maintenance-execution/useWorkOrderMutations';
import { useWorkOrderKanbanLookups } from '@evimo/components/maintenance/work-orders/WorkOrderKanbanPageContainer/hooks/useWorkOrderKanbanLookups';
import {
  formatTicketDueDate,
  isCurativeTicketType,
  canQualifyInterventionOnKanban,
  isWorkOrderEligibleToClose,
  priorityTagColor,
  ticketDueColor,
  ticketDueUrgency,
  ticketStatusTagColor,
  ticketTypeTagColor,
} from '@evimo/components/maintenance/work-orders/WorkOrderKanbanPageContainer/utils';
import { TicketCurativeContext } from '@evimo/components/maintenance/work-orders/WorkOrderKanbanPageContainer/components/TicketCurativeContext/TicketCurativeContext';
import { TicketTimelineCollapse } from '@evimo/components/maintenance/work-orders/WorkOrderKanbanPageContainer/components/TicketTimelineCollapse/TicketTimelineCollapse';
import {
  TicketReportSnippet,
  ticketHasReport,
} from '@evimo/components/maintenance/work-orders/WorkOrderKanbanPageContainer/components/TicketReportSnippet/TicketReportSnippet';

export interface WorkOrderDetailDrawerProps {
  workOrderId: string | null;
  workOrderCode?: string | null;
  open: boolean;
  onClose: () => void;
  canManage: boolean;
  onMoveTicket: (ticketId: string) => void;
  onCreateNewWorkOrder: (ticketId: string) => void;
}

export const WorkOrderDetailDrawer: FC<WorkOrderDetailDrawerProps> = ({
  workOrderId,
  workOrderCode,
  open,
  onClose,
  canManage,
  onMoveTicket,
  onCreateNewWorkOrder,
}) => {
  const { t } = useTranslation('maintenance');
  const { user } = useAuth();
  const organizationId = user?.organizationId ?? '';
  const lookups = useWorkOrderKanbanLookups(organizationId);
  const mutations = useWorkOrderMutations(organizationId);
  const detailQuery = useWorkOrderDetail({
    workOrderId: open ? workOrderId : null,
    organizationId,
    siteNameById: lookups.siteNameById,
    technicianNameById: lookups.technicianNameById,
    unknownSiteLabel: t('workOrders.kanban.unknownSite'),
    unknownTechnicianLabel: t('workOrders.kanban.unknownTechnician'),
  });

  const workOrder = detailQuery.data ?? null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size={420}
      title={
        workOrder ? (
          <Space>
            <span>{workOrder.code}</span>
            {workOrder.externalIdentifier ? (
              <Tag
                style={{
                  margin: 0,
                  ...softBadgeStyle(WORK_ORDER_UI.ticketType.CURATIVE),
                }}
              >
                {t('workOrders.kanban.externalOt', {
                  ot: workOrder.externalIdentifier,
                })}
              </Tag>
            ) : null}
            <Tag color={priorityTagColor(workOrder.highestPriority)}>
              {t(`workOrders.priority.${workOrder.highestPriority}`)}
            </Tag>
            <Tag>{t(`workOrders.status.${workOrder.status}`)}</Tag>
            {canManage && isWorkOrderEligibleToClose(workOrder) ? (
              <Button
                size="small"
                type="primary"
                loading={mutations.closeWorkOrder.isPending}
                onClick={() => {
                  if (!workOrderId) return;
                  mutations.closeWorkOrder.mutate(workOrderId);
                }}
              >
                {t('workOrders.kanban.detail.closeWorkOrder')}
              </Button>
            ) : null}
          </Space>
        ) : (
          (workOrderCode ?? t('workOrders.kanban.detail.loading'))
        )
      }
    >
      {detailQuery.isError ? (
        <Alert type="error" title={t('workOrders.kanban.detail.loadFailed')} />
      ) : null}
      {detailQuery.isLoading || !workOrder ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : (
        <Space orientation="vertical" size={16} style={{ width: '100%' }}>
          <div>
            <Typography.Text type="secondary">
              {t('workOrders.kanban.detail.site')}
            </Typography.Text>
            <div>{workOrder.siteName}</div>
          </div>
          <div>
            <Typography.Text type="secondary">
              {t('workOrders.kanban.detail.technician')}
            </Typography.Text>
            <div>
              {workOrder.assignedTechnicianName ??
                t('workOrders.kanban.detail.unassigned')}
            </div>
          </div>
          <div>
            <Typography.Title level={5}>
              {t('workOrders.kanban.detail.tickets')}
            </Typography.Title>
            <Space orientation="vertical" style={{ width: '100%' }} size={10}>
              {workOrder.tickets.map((ticket) => (
                <div
                  key={ticket.id}
                  style={{
                    border: '1px solid #e2e8f0',
                    borderRadius: 8,
                    padding: 10,
                  }}
                >
                  <Space wrap>
                    <Typography.Text strong>{ticket.code}</Typography.Text>
                    <Tag color={ticketStatusTagColor(ticket.status)}>
                      {t(`workOrders.ticketStatus.${ticket.status}`, {
                        defaultValue: ticket.status,
                      })}
                    </Tag>
                    <Tag color={ticketTypeTagColor(ticket.type)}>
                      {t(`workOrders.ticketType.${ticket.type}`)}
                    </Tag>
                    {ticket.lastExecutionResult ? (
                      <Tag
                        style={{
                          margin: 0,
                          fontWeight: 700,
                          ...softBadgeStyle(
                            executionResultTone(ticket.lastExecutionResult),
                          ),
                        }}
                      >
                        {t('workOrders.kanban.lastResult', {
                          result: t(
                            `workOrders.executionResult.${ticket.lastExecutionResult}`,
                            {
                              defaultValue: ticket.lastExecutionResult,
                            },
                          ),
                        })}
                      </Tag>
                    ) : null}
                    {ticket.needsFollowUp ? (
                      <Tag color="gold">
                        {t('workOrders.kanban.needsFollowUp')}
                      </Tag>
                    ) : null}
                  </Space>
                  <TicketCurativeContext
                    type={ticket.type}
                    planName={ticket.planName}
                    planCode={ticket.planCode}
                    ticketId={ticket.id}
                    ticketTitle={ticket.title}
                    organizationId={organizationId || undefined}
                    incidentId={ticket.incidentId}
                    incidentCode={ticket.incidentCode}
                    incidentTitle={ticket.incidentTitle}
                    summaryMaxRows={6}
                    onBeforeNavigate={onClose}
                  />
                  {ticket.assetName || ticket.assetSerialNumber ? (
                    <Typography.Text style={{ display: 'block', marginTop: 4 }}>
                      {t('workOrders.kanban.assetLabel', {
                        name: ticket.assetName ?? '—',
                        serial: ticket.assetSerialNumber ?? '—',
                      })}
                    </Typography.Text>
                  ) : null}
                  {(() => {
                    const dueLabel = formatTicketDueDate(ticket.dueAt);
                    const dueUrgency = ticketDueUrgency(ticket.dueAt);
                    if (!dueLabel || !dueUrgency) return null;
                    return (
                      <Typography.Text
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          marginTop: 6,
                          fontWeight: dueUrgency === 'normal' ? 600 : 700,
                          color: ticketDueColor(dueUrgency),
                        }}
                      >
                        <CalendarOutlined />
                        {dueUrgency === 'overdue'
                          ? t('workOrders.kanban.dueOverdue', {
                              date: dueLabel,
                            })
                          : dueUrgency === 'soon'
                            ? t('workOrders.kanban.dueSoon', { date: dueLabel })
                            : t('workOrders.kanban.dueLabel', {
                                date: dueLabel,
                              })}
                      </Typography.Text>
                    );
                  })()}
                  <Typography.Paragraph style={{ margin: '6px 0' }}>
                    {ticket.title}
                  </Typography.Paragraph>
                  {ticketHasReport(ticket.report) ? (
                    <TicketReportSnippet report={ticket.report} />
                  ) : null}
                  {ticket.subtaskNames.length > 0 ? (
                    <div style={{ marginBottom: 8 }}>
                      <PlanDetailTaskSubtasks
                        taskId={ticket.taskId ?? ticket.id}
                        subtaskNames={ticket.subtaskNames}
                      />
                    </div>
                  ) : null}
                  {mutations.closeIntervention.isError &&
                  mutations.closeIntervention.variables?.ticketId ===
                    ticket.id ? (
                    <Alert
                      type="error"
                      title={t(
                        'workOrders.kanban.detail.closeInterventionFailed',
                      )}
                      style={{ marginBottom: 8 }}
                    />
                  ) : null}
                  <Space wrap size={8} style={{ marginTop: 4 }}>
                    {!isCurativeTicketType(ticket.type) ? (
                      <Button
                        type="primary"
                        size="small"
                        icon={<EyeOutlined />}
                        style={{
                          background: WORK_ORDER_UI.primary,
                          borderColor: WORK_ORDER_UI.primary,
                        }}
                        onClick={() =>
                          window.open(
                            `/maintenance/work-orders/tickets/${ticket.id}`,
                            '_blank',
                            'noopener,noreferrer',
                          )
                        }
                      >
                        {t('workOrders.kanban.detail.viewTicket')}
                      </Button>
                    ) : null}
                    {canManage &&
                    canQualifyInterventionOnKanban(workOrder, {
                      status: ticket.status,
                      needsFollowUp: ticket.needsFollowUp,
                      lastExecutionResult: ticket.lastExecutionResult,
                    }) ? (
                      <Button
                        size="small"
                        loading={
                          mutations.closeIntervention.isPending &&
                          mutations.closeIntervention.variables?.ticketId ===
                            ticket.id
                        }
                        onClick={() => {
                          if (!workOrderId) return;
                          mutations.closeIntervention.mutate({
                            workOrderId,
                            ticketId: ticket.id,
                          });
                        }}
                      >
                        {t('workOrders.kanban.detail.closeIntervention')}
                      </Button>
                    ) : null}
                    {canManage && ticket.needsFollowUp ? (
                      <>
                        <Button
                          size="small"
                          type="default"
                          icon={<PlusOutlined />}
                          onClick={() => onCreateNewWorkOrder(ticket.id)}
                        >
                          {t('workOrders.kanban.createNewWorkOrder')}
                        </Button>
                        <Button
                          size="small"
                          icon={<SwapOutlined />}
                          onClick={() => onMoveTicket(ticket.id)}
                        >
                          {t('workOrders.kanban.move.modeExisting')}
                        </Button>
                      </>
                    ) : null}
                  </Space>
                  <TicketTimelineCollapse ticketId={ticket.id} />
                </div>
              ))}
            </Space>
          </div>
        </Space>
      )}
    </Drawer>
  );
};
